/**
 * ตั้งค่าการแจ้งเตือน (ระดับบริษัท) — owner/HR เปิด/ปิดการแจ้งเตือนอัตโนมัติแต่ละแบบ
 * + master "ปิดเสียงในวันหยุด". ปุ่มกดส่งเอง (เช่น ส่งสลิป/เชิญ) ไม่กระทบ.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { BellOff } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { apiFetch } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

interface Prefs {
  checkin_scan: boolean;
  briefing: boolean;
  cutoff_reminder: boolean;
  approval_escalation: boolean;
  profile_followup: boolean;
  clockout_prompt: boolean;
  mute_on_holiday: boolean;
  owner_leave_approved: boolean;
  owner_ot_approved: boolean;
  owner_request_submitted: boolean;
}

// เจ้าของรับทราบเมื่อ HR อนุมัติคำขอเสร็จ (flex card "รับทราบ")
const OWNER_ITEMS: Array<{ key: keyof Prefs; label: string; desc: string }> = [
  { key: 'owner_request_submitted', label: 'รับแจ้งทุกครั้งที่มีคำขอใหม่', desc: 'แจ้งเจ้าของเข้า LINE ทุกครั้งที่พนักงานยื่นขอลา / OT / ย้ายวันหยุด (ทุกเคส)' },
  { key: 'owner_leave_approved', label: 'รับแจ้งเมื่ออนุมัติใบลาเสร็จ', desc: 'ส่งการ์ดรับทราบให้เจ้าของทุกครั้งที่ใบลาผ่านการอนุมัติครบ (หลัง HR)' },
  { key: 'owner_ot_approved', label: 'รับแจ้งเมื่ออนุมัติ OT เสร็จ', desc: 'ส่งการ์ดรับทราบให้เจ้าของทุกครั้งที่ใบ OT ผ่านการอนุมัติครบ (หลัง HR)' },
];

const ITEMS: Array<{ key: keyof Prefs; label: string; desc: string }> = [
  { key: 'checkin_scan', label: 'เตือนสแกนเข้า-ออกงาน', desc: 'เตือนพนักงานให้สแกนหน้าเข้า/ออก (ข้ามวันหยุด + วันหยุดรายคนอัตโนมัติ)' },
  { key: 'briefing', label: 'สรุปงานวันนี้', desc: 'ส่งสรุปงานประจำวันให้เจ้าของ/HR/หัวหน้า ทุกเช้า' },
  { key: 'cutoff_reminder', label: 'เตือนใกล้ปิดรอบ / คำขอค้าง', desc: 'เตือนเจ้าของ/HR เมื่อใกล้ปิดรอบเงินเดือน และมีคำขอค้างอนุมัติ' },
  { key: 'approval_escalation', label: 'เร่งคำขอ/ลาที่ค้างเกินกำหนด', desc: 'แจ้งผู้อนุมัติเมื่อคำขอ/ลาฉุกเฉินค้างนานเกินกำหนด (SLA)' },
  { key: 'profile_followup', label: 'ตามให้พนักงานกรอกประวัติ', desc: 'เตือนพนักงานที่กรอกข้อมูลส่วนตัวไม่ครบ' },
  { key: 'clockout_prompt', label: 'เตือนลืมออกงาน (clock-out)', desc: 'เตือนพนักงานที่ลืมสแกนออกงานเมื่อวาน' },
];

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? 'bg-brand-600' : 'bg-gray-300'}`}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? 'translate-x-5' : ''}`} />
    </button>
  );
}

export function NotificationSettings() {
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => apiFetch<{ prefs: Prefs }>('/owner/notification-settings'),
  });
  const save = useMutation({
    mutationFn: (prefs: Prefs) => apiFetch<{ prefs: Prefs }>('/owner/notification-settings', { method: 'POST', body: { prefs } }),
  });
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  useEffect(() => { if (data?.prefs) setPrefs(data.prefs); }, [data]);

  function toggle(k: keyof Prefs) {
    setPrefs((p) => (p ? { ...p, [k]: !p[k] } : p));
  }
  async function onSave() {
    if (!prefs) return;
    try {
      await save.mutateAsync(prefs);
      toast.success('บันทึกการตั้งค่าแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ตั้งค่าการแจ้งเตือน">
      <div className="max-w-2xl mx-auto space-y-4 pb-10">
        <p className="text-sm text-gray-500 px-1">
          เปิด/ปิดการแจ้งเตือนอัตโนมัติของบริษัท · ปุ่มกดส่งเอง (เช่น ส่งสลิป / เชิญพนักงาน) ไม่ได้รับผลกระทบ
        </p>

        {isLoading || !prefs ? (
          <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
        ) : (
          <>
            {/* master: mute on holiday */}
            <div className="card p-4 flex items-start justify-between gap-3 bg-amber-50/50 border-amber-200">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-1.5"><BellOff className="w-4 h-4 text-amber-600" /> ปิดเสียงทุกอย่างในวันหยุด</div>
                <p className="text-xs text-gray-500 mt-0.5">วันหยุดบริษัท + เสาร์-อาทิตย์ ไม่ส่งการแจ้งเตือนอัตโนมัติใดๆ (ใช้ตารางวันหยุดเดียวกับเงินเดือน)</p>
              </div>
              <Toggle on={prefs.mute_on_holiday} onClick={() => toggle('mute_on_holiday')} />
            </div>

            <div className="card divide-y divide-gray-100">
              {ITEMS.map((it) => (
                <div key={it.key} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{it.label}</div>
                    <p className="text-xs text-gray-500 mt-0.5">{it.desc}</p>
                  </div>
                  <Toggle on={prefs[it.key]} onClick={() => toggle(it.key)} />
                </div>
              ))}
            </div>

            {/* เจ้าของรับทราบเมื่ออนุมัติเสร็จ */}
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1 pt-2">เจ้าของรับทราบเมื่ออนุมัติเสร็จ</h2>
            <div className="card divide-y divide-gray-100">
              {OWNER_ITEMS.map((it) => (
                <div key={it.key} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{it.label}</div>
                    <p className="text-xs text-gray-500 mt-0.5">{it.desc}</p>
                  </div>
                  <Toggle on={prefs[it.key]} onClick={() => toggle(it.key)} />
                </div>
              ))}
            </div>

            <button onClick={onSave} disabled={save.isPending} className="btn-primary w-full justify-center">
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึกการตั้งค่า'}
            </button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
